import { createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import {
  browserSoundRuntime,
  playNotificationSound,
  readStoredNotificationSound,
  type SoundChoice,
  type SoundRuntime,
  writeStoredNotificationSound,
} from '../../lib/notification-sound';
import { Field } from '../Field';
import { Panel } from '../Panel';
import { Select, type SelectOption } from '../Select';
import { Button } from '../ui/Button';

// The Notifications page's per-device sound section (#5890): a labelled
// themed `Select` (Off, Chime, Ping, Pop) and a "Test sound" button that
// plays the selected tone. The Select writes the stored choice immediately,
// and the stored value is read on mount through `readStoredNotificationSound`
// so a hard-reload paints the page with the saved pick.
//
// The button is disabled while Off, with the reason as text, so it never reads as
// a dead control. The Select writes the stored choice at once, so the Test sound
// button plays the choice on screen.
//
// `playNotificationSound` returns `false` for the autoplay block (a context
// that stays `suspended` until the user has interacted with the page) and
// the page's hint line says so. While that block is on, the `blocked`
// sentence renders as a `role="status"` line under the row.

const FIELD_ID = 'notification-sound';

/** The order the `Select` lists. `off` is the default — first. */
const ORDER: readonly SoundChoice[] = ['off', 'chime', 'ping', 'pop'] as const;

export function NotificationSound(props: { makeRuntime?: () => SoundRuntime } = {}): JSX.Element {
  const [stored, setStored] = createSignal<SoundChoice>(readStoredNotificationSound());
  // `blocked` is the post-test notice: a `playNotificationSound` returned
  // `false` after a Test press. Cleared on the next pick so a successful
  // tone after a blocked one does not leave the line showing.
  const [blocked, setBlocked] = createSignal<boolean>(false);

  const makeRuntime = (): SoundRuntime =>
    props.makeRuntime ? props.makeRuntime() : browserSoundRuntime();

  const onChange = (value: SoundChoice): void => {
    writeStoredNotificationSound(value);
    setStored(value);
    setBlocked(false);
  };

  const playSelected = async (): Promise<void> => {
    setBlocked(false);
    if (!(await playNotificationSound(stored(), makeRuntime()))) setBlocked(true);
  };

  const options = (): readonly SelectOption<SoundChoice>[] =>
    ORDER.map((value) => ({ value, label: t(`notifications_settings.sound.${value}`) }));
  const isOff = createMemo((): boolean => stored() === 'off');

  return (
    <Panel title={t('notifications_settings.sound.title')}>
      <Field id={FIELD_ID} label={t('notifications_settings.sound.label')}>
        {(a11y) => (
          <Select
            a11y={a11y}
            label={t('notifications_settings.sound.label')}
            block
            value={stored()}
            options={options()}
            onChange={onChange}
          />
        )}
      </Field>
      <div class="notification-sound__row">
        <Button
          variant="outline"
          size="sm"
          disabled={isOff()}
          aria-disabled={isOff()}
          title={isOff() ? t('notifications_settings.sound.off') : undefined}
          onClick={() => void playSelected()}
        >
          {t('notifications_settings.sound.sound_test')}
        </Button>
      </div>
      <Show when={isOff()}>
        <p class="notification-sound__reason">{t('notifications_settings.sound.off')}</p>
      </Show>
      <Show when={blocked()}>
        <p role="status" class="notification-sound__blocked">
          {t('notifications_settings.sound.blocked')}
        </p>
      </Show>
      <p class="notification-sound__hint">{t('notifications_settings.sound.hint')}</p>
    </Panel>
  );
}
